import { createPath } from "@/service/routing/routing";
import { SuperLink as Link } from "@/components/common/super-link";

const intro = (
  <p>
    <strong>Wyszukiwarka Leków</strong> pozwala sprawdzić informacje o lekach,
    uwzględniając nazwę handlową, substancję czynną, wskazania terapeutyczne
    oraz dane na temat kosztów leczenia i refundacji. W oparciu o informacje
    pochodzące z <strong>Bazy Leków</strong> {""}
    Remedium, Wyszukiwarka Leków dostarcza użytkownikowi także informacje na
    temat dostępnych zamienników, wskazań refundacyjnych, bezpieczeństwa
    stosowania produktu u kobiet w ciąży czy ryzyka wystąpienia potencjalnych
    interakcji. Wyszukiwarka Leków stanowi użyteczne narzędzie w praktyce
    codziennej lekarza, które zostało zintegrowane z pozostałymi
    funkcjonalnościami Remedium:{" "}
    <a href={createPath("meds.index")} target="_blank" rel="noreferrer">
      Wyszukiwarką ICD-10
    </a>{" "}
    oraz{" "}
    <a href={createPath("tutorial.index")} target="_blank" rel="noreferrer">
      Poradnikami medycznymi
    </a>
    .
  </p>
);

const content = (
  <>
    <h3>Leki refundowane – czym są?</h3>
    <p>
      <strong>Leki refundowane</strong> są to produkty lecznicze dopuszczone do
      obrotu w Polsce, których koszt zakupu może zostać częściowo lub całkowicie
      pokryty przez Narodowy Fundusz Zdrowia (NFZ). O tym, czy lek jest
      refundowany, a także o wysokości potencjalnej refundacji decyduje Minister
      Zdrowia, umieszczając produkt leczniczy w wykazie leków refundowanych.
      Wykaz leków refundowanych ukazuje się co dwa miesiące. Do pokrycia kosztów
      leczenia kwalifikują się jedynie pacjenci, którzy spełniają kryteria
      zawarte w wykazie leków refundowanych.
    </p>

    <h3>Wyszukiwarka leków refundowanych</h3>
    <p>
      Informacje na temat refundacji oraz wskazań refundacyjnych można znaleźć
      przy każdym produkcie, dostępnym w <strong>Wyszukiwarce Leków</strong>.
      Dzięki tej funkcji, a także regularnie aktualizowanej{" "}
      <strong>Bazie Leków</strong>, Wyszukiwarka Leków może posłużyć również
      jako <strong>wyszukiwarka leków refundowanych</strong>. Informacje o cenie
      i refundacji znajdują się na karcie leku. Wyszukiwarka jest także dostępna
      w formie <strong>mobilnej wyszukiwarki leków refundowanych</strong>. Po
      uruchomieniu strony w przeglądarce, interfejs strony zostanie
      automatycznie dopasowany do rozdzielczości aktualnie używanego urządzenia.
      Szerzej o możliwościach i funkcjonowaniu Wyszukiwarki Leków pisaliśmy w
      naszym e-booku{" "}
      <a
        href="https://remedium.md/ebook/pierwsze-kroki-w-poz"
        target="_blank"
        rel="noreferrer"
      >
        Pierwsze kroki w POZ (Edycja 2022) dostępnym do pobrania bezpłatnie
      </a>
      .
    </p>

    <h3>Leki refundowane dla kobiet w ciąży</h3>
    <p>
      W wykazie leków refundowanych opublikowanych przez Ministerstwo Zdrowia
      można znaleźć grupy pacjentów uprawnionych do bezpłatnych leków.
      Refundacji podlegają <strong>leki dla honorowych dawców krwi</strong>,
      kobiet w ciąży oraz dla seniorów. Informacje o lekach bezpłatnych dla
      dwóch ostatnich grup można także uzyskać za pośrednictwem{" "}
      <strong>Wyszukiwarki Leków</strong>. Jeżeli lek jest bezpłatny dla kobiet
      ciężarnych, na karcie leku widnieje symbol „C”. Leki bezpłatne dla
      seniorów, czyli pacjentów po 65. roku życia oznaczono symbolem 65+.
    </p>

    <h3>Wyszukiwarka interakcji leków</h3>
    <p>
      Interakcje mogą zwiększać ryzyko wystąpienia działań ubocznych stosowanych
      leków lub zmniejszać skuteczność ich działania.{" "}
      <strong>Wyszukiwarka interakcji leków</strong> jest to funkcja wbudowana w{" "}
      <strong>Wyszukiwarkę Leków</strong>. Jest on szczególnie przydatna w
      przypadku stosowania przez pacjenta więcej niż jednego lekarstwa.
      Interakcje lekowe można sprawdzić z poziomu karty leku lub wchodząc
      bezpośrednio na stronę{" "}
      <a
        href={createPath("meds.interactions")}
        target="_blank"
        rel="noreferrer"
      >
        Interakcje lekowe
      </a>
      , dodając kolejno stosowane przez pacjenta leki. Ryzyko wystąpienia
      interakcji lekowych znacznie wzrasta u osób starszych, ze względu na
      przyjmowaną przez tę grupę pacjentów dużą liczbę leków. Szerzej o
      problemie tzw. wielolekowości pisaliśmy w tym artykule:{" "}
      <Link href="https://remedium.md/publikacje/case-studies/opieka-nad-pacjentem-starszym-z-wielochorobowoscia-co-warto-wiedziec">
        „Opieka nad pacjentem starszym z wielochorobowością - co warto
        wiedzieć?”
      </Link>
      . Przykładowe <strong>interakcje</strong>:
    </p>

    <ul>
      <li>
        połączenie leków przeciwbólowych z różnych grup -{" "}
        <a
          href="https://remedium.md/wyszukiwarka-lekow/interakcje?inns=917&groups=10477211"
          target="_blank"
          rel="noreferrer"
        >
          pyralgina i paracetamol
        </a>
      </li>
      <li>
        połączenie leku przeciwbólowego z lekiem hipotensyjnym –{" "}
        <a
          href="https://remedium.md/wyszukiwarka-lekow/interakcje?groups=&inns=606%2C701"
          target="_blank"
          rel="noreferrer"
        >
          ibuprofen i lisinopril
        </a>
      </li>
    </ul>

    <h3>Leki od A do Z</h3>
    <p>
      <strong>Indeks Leków</strong> przedstawia pełen{" "}
      <strong>wykaz leków</strong> znajdujących się w bazie Remedium. Indeks
      uruchamia się w sposób automatyczny po otwarciu okna Wyszukiwarki Leków i
      wyświetla znajdujące się tam <strong>leki od A do Z</strong>. Wyświetlona{" "}
      <strong>lista leków</strong> może być przeszukiwana na różne sposoby, np.
      wpisując pełną nazwę handlową leku lub nazwę międzynarodową substancji
      czynnej. Indeks Leków pozwala również na fragmentaryczne wyszukiwanie
      interesujących nas produktów. Po wprowadzeniu pierwszych liter nazwy,
      lista substancji czynnych i produktów zostanie zawężona, a wyszukiwanie
      ułatwione.
    </p>
  </>
);

export const medsDescription = { intro, content };
